import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { CheckCircle2, XCircle, AlertTriangle, FileText } from 'lucide-react';
import { format } from 'date-fns';
import type { ImportSummary, ImportLog, ImportException } from '../../../lib/api/barratt';
import { spreadsheetRow } from '../uploadCycle';
import ImportStatusBadge from './ImportStatusBadge';

interface ImportDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  importSummary: ImportSummary | null;
  logs: ImportLog[];
  exceptions: ImportException[];
}

const SEVERITY_CLASS: Record<string, string> = {
  error: 'bg-red-50 text-red-700 border-red-200',
  warning: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
};

/** Per-page processing logs and row-level exceptions for one import. */
export default function ImportDetailsDialog({ open, onOpenChange, importSummary, logs, exceptions }: ImportDetailsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Details #{importSummary?.id}</DialogTitle>
          <DialogDescription>View import logs and exceptions</DialogDescription>
        </DialogHeader>

        {importSummary && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">Total Records</p>
                <p className="text-lg font-semibold">{importSummary.total_records}</p>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">Added</p>
                <p className="text-lg font-semibold text-green-600">{importSummary.total_added ?? 0}</p>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">Failed</p>
                <p className="text-lg font-semibold text-red-600">{importSummary.total_failed ?? 0}</p>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">Pages</p>
                <p className="text-lg font-semibold">
                  {importSummary.imported_pages}/{importSummary.total_pages}
                </p>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground">Status</p>
                <ImportStatusBadge {...importSummary} />
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Processing Logs
              </h4>
              {logs.length === 0 ? (
                <p className="text-muted-foreground text-sm">No logs available</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Page</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Added</TableHead>
                      <TableHead>Updated</TableHead>
                      <TableHead>Skipped</TableHead>
                      <TableHead>Failed</TableHead>
                      <TableHead>Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>Page {log.page_index + 1}</TableCell>
                        <TableCell>
                          {log.is_success ? (
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                        </TableCell>
                        <TableCell className="text-green-600">{log.added}</TableCell>
                        <TableCell className="text-blue-600">{log.updated}</TableCell>
                        <TableCell className="text-yellow-600">{log.skipped}</TableCell>
                        <TableCell className="text-red-600">{log.failed}</TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {log.finish_time ? format(new Date(log.finish_time), 'HH:mm:ss') : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            {exceptions.length > 0 && (
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2 text-red-600">
                  <AlertTriangle className="h-4 w-4" />
                  Exceptions ({exceptions.length})
                </h4>
                <div className="max-h-64 overflow-y-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-24">Row in file</TableHead>
                        <TableHead>Column</TableHead>
                        <TableHead>Error Code</TableHead>
                        <TableHead>Severity</TableHead>
                        <TableHead>Message</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {exceptions.map((exc) => (
                        <TableRow key={exc.id}>
                          {/* The importer counts data rows from 1, so its row 1
                              is row 2 of the spreadsheet the person will open. */}
                          <TableCell className="font-mono">{spreadsheetRow(exc.row_number) ?? 'Header'}</TableCell>
                          <TableCell className="font-mono text-sm">{exc.column_name || '-'}</TableCell>
                          <TableCell>
                            {exc.error_code && (
                              <Badge variant="secondary" className="font-mono text-xs">{exc.error_code}</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={SEVERITY_CLASS[exc.severity] ?? SEVERITY_CLASS['info']}>
                              {exc.severity}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-[300px]">
                            <span className="line-clamp-2">{exc.message}</span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
